import {
  DevLanguageSwitcher,
  displayLanguageSwitcher,
  ErrorMessage,
  howWeCalculate,
  MoreInformation,
  ResetSitecoreStyles,
  thingsToConsider,
} from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { BaseLayout, FormLayout, ResultLayout } from '@hex/calculators-shared/layout';
import { trackCalculatorSubmit } from '@hex/shared/tracking';
import { defaultTheme, SapphireProvider, Flex, View, tokens } from '@sapphire-react/core';
import { useRef, useState } from 'react';
import type { UseFormReset } from 'react-hook-form';
import styled from 'styled-components';
import { MINIMUM_LOAN_VALUE } from '../constants';
import { fetchAffordability } from './api';
import { AffordabilityInputs } from './components/Form/AffordabilityInputs';
import { Results } from './components/Results';
import { DesktopLoader } from './components/Results/DesktopLoader';
import ErrorCard from './components/Results/ErrorCard';
import { MobileLoader } from './components/Results/MobileLoader';
import { TipsContainer } from './components/Results/TipsContainer';
import { AffordabilityFormDataType, AffordabilityResponse } from './types';

interface StyledViewProps {
  isDisabled?: boolean;
}

export function App() {
  const [affordabilityResponseData, setAffordabilityResponseData] = useState<AffordabilityResponse | null>(null);
  const [, setFormData] = useState<AffordabilityFormDataType>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResultSectionHidden, setIsResultSectionHidden] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isEligibleForLoan, setIsEligibleForLoan] = useState<boolean>(true);
  const [hasCalculated, setHasCalculated] = useState<boolean>(false);
  const baseLayoutRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isExtraSmall, isSmall } = useSapphireMedia();
  const isDesktop = !isExtraSmall && !isSmall;

  const scrollIntoCalculatorTop = () => {
    if (isDesktop) {
      const sitecoreNavbarHeight = 102;
      const top = baseLayoutRef?.current
        ? baseLayoutRef?.current?.getBoundingClientRect().top + window.scrollY - sitecoreNavbarHeight
        : undefined;

      window?.scrollTo({ top });
    }
  };

  async function onSubmit(data: AffordabilityFormDataType, reset?: UseFormReset<AffordabilityFormDataType>) {
    setIsLoading(true);
    setIsResultSectionHidden(false);
    setHasError(false);
    setIsDisabled(true);

    try {
      const affordability = await fetchAffordability(data);
      setFormData(data);

      setAffordabilityResponseData(affordability);
      trackCalculatorSubmit(affordability.loanAmount.toString(), JSON.stringify(data));

      setIsEligibleForLoan(affordability.loanAmount >= MINIMUM_LOAN_VALUE);
      setIsLoading(false);
      setIsDisabled(false);
      setHasCalculated(true);

      reset?.(data, { keepValues: true });
    } catch (_e) {
      setIsLoading(false);
      setIsResultSectionHidden(true);
      setHasError(true);
      setIsDisabled(false);
    }

    scrollIntoCalculatorTop();
  }

  const handleFormChange = () => {
    setAffordabilityResponseData(null);
    setIsDisabled(true);
  };

  return (
    <ResetSitecoreStyles>
      <span
        style={{ display: 'none' }}
        data-build-id={import.meta.env.NX_PUBLIC_BUILD_NUMBER}
        data-git-hash={import.meta.env.NX_PUBLIC_GIT_HASH}
      />
      <SapphireProvider theme={defaultTheme} locale={'fi-FI'} width={'100%'} overlayZIndex={1000} position={'relative'}>
        {displayLanguageSwitcher && <DevLanguageSwitcher languages={['fi', 'en']} />}
        <Flex justifyContent={'center'} width={'100%'}>
          <View
            marginBottom={{ base: 16, sm: 40, md: 72 }}
            width={{ base: '100%', sm: 688, md: 996 }}
            paddingLeft={{ base: 8, sm: 0 }}
            paddingRight={{ base: 8, sm: 0 }}
          >
            <BaseLayout ref={baseLayoutRef} columnGap={24} border={'none'}>
              {!isDesktop && isLoading && <MobileLoader />}
              <Flex justifyContent={'center'} width={'100%'}>
                <View minWidth={{ base: '100%', sm: 336, md: 486 }}>
                  <FormLayout borderRadius={8}>
                    <AffordabilityInputs
                      onSubmit={onSubmit}
                      buttonRef={buttonRef}
                      affordabilityResponseData={affordabilityResponseData}
                      onFormChange={handleFormChange}
                      loading={isLoading}
                      isDisabled={isDisabled}
                    />
                  </FormLayout>
                </View>
              </Flex>
              <Flex justifyContent={'center'} width={'100%'}>
                <View minWidth={{ base: '100%', sm: 336, md: 486 }} position={'relative'}>
                  {isDesktop && isLoading && <DesktopLoader />}
                  <ErrorCard isHidden={!hasError} />
                  <StyledView
                    aria-disabled={isDisabled}
                    isDisabled={isDisabled}
                    style={{ display: isEligibleForLoan ? 'block' : 'none' }}
                  >
                    <TipsContainer isHidden={!isResultSectionHidden || hasError} />
                    <ResultLayout
                      isHidden={!hasCalculated || (isResultSectionHidden && hasError)}
                      withPadding={false}
                      withSeparator={false}
                      borderRadius={8}
                      withBorder={false}
                    >
                      <Results
                        affordabilityResponseData={affordabilityResponseData}
                        hasError={hasError}
                        isDisabled={isDisabled}
                        loading={isLoading}
                        onError={() => setHasError(true)}
                      />
                    </ResultLayout>
                  </StyledView>
                  {isEligibleForLoan ? (
                    !isDisabled &&
                    !hasError &&
                    hasCalculated && (
                      <View
                        marginLeft={{ base: 0, sm: 8, md: 12 }}
                        marginRight={{ base: 0, sm: 8, md: 12 }}
                        marginTop={{ base: -30, sm: -36 }}
                      >
                        <MoreInformation items={[howWeCalculate, thingsToConsider]} hideLastDivider={false} />
                      </View>
                    )
                  ) : (
                    <StyledView aria-disabled={isLoading} isDisabled={isLoading}>
                      <ErrorMessage variant={'informative'} />
                    </StyledView>
                  )}
                </View>
              </Flex>
            </BaseLayout>
          </View>
        </Flex>
      </SapphireProvider>
    </ResetSitecoreStyles>
  );
}

const StyledView = styled.div<StyledViewProps>`
  padding-bottom: ${tokens.size.spacing2xl.value};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  & * {
    ${({ isDisabled }) => isDisabled && 'tab-index: -1;'}
  }
`;
